/**
 * Column definitions and row builders for the single-order Documents table
 * (J-codes, document lists, upload icons, and auth-status–driven doc sets).
 *
 * @module documentsTable
 */
import {
  TableCellType,
  TableHeader,
} from "../../../../components/custom-table/table";
import { authStatusForDisablingTriggerRpa } from "../../../../constants/authStatusForDisablingTriggerRpa";
import {
  getDocumentsUploadStatusForStatus,
  statusToDocumentUploadMap,
} from "../../../../constants/statusToDocMap/docUploadMap";
import { getDocumentsForStatus } from "../../../../constants/statusToDocMap/statusToDocMap";
import { Authorization } from "../../../../data-model/AuthorizationModel";
import {
  AuthOnFile,
  GeneralOrderDetails,
  JCodeDetails,
} from "../../../../data-model/medicalPaOrdersModel";
import { DocumentTypesForOrder } from "../../../../enums/documentTypes";
import { DocWriteBackStatus } from "../../../../enums/evBvWriteBackStatus";
import { CptCodeWithDetailsKeys } from "../../../../enums/tableColumnKeys";
import { UserGeneralInfoContextKeys } from "../../../../enums/userGeneralInfoContextKeys";
import { UserStore } from "../../../../store/UserGeneralInfoStore";
import { getDocumentUploadStatusForJcode } from "../../../../utils/documentUploadStatus";

import {
  checkifAllDocsAreUploaded,
  getDocsNotBelongingToCurrentAuthStatus,
} from "../../../../utils/getDocsFromStatus";

/** Custom-table header config for the documents tab (J-code, docs, upload column). */
export const CptCodesTableColumnsKeys: TableHeader[] = [
  {
    label: "J Codes",
    key: CptCodeWithDetailsKeys.JCodes,
    order: 1,
    width: 15,
    type: TableCellType.STRING,
  },
  {
    label: "",
    key: CptCodeWithDetailsKeys.Documents,
    order: 2,
    width: 80,
    type: TableCellType.STRING_WITH_BUTTON,
  },
  {
    label: "",
    key: CptCodeWithDetailsKeys.DocWithUploadIcon,
    order: 3,
    width: 5,
    type: TableCellType.DOC_WITH_UPLOAD_ICON,
  },
];

/**
 * Builds one table row per J-code: label, document cell payload, and upload-icon
 * state derived from auth status and upload maps. Persists medicine names to {@link UserStore}.
 *
 * @param generalOrderDetails - Order demographics, ids, and document upload status
 * @param authOnFileDetails - Auth entries and per–J-code documents/statuses
 * @returns Row objects keyed by {@link CptCodeWithDetailsKeys} for the documents table
 */
export const generateDocumentsTableData = (
  generalOrderDetails: GeneralOrderDetails,
  authOnFileDetails: AuthOnFile,
  authorizationData: Authorization | null,
  authorizationDataLoading: boolean,
) => {
  const authEntries = authOnFileDetails?.entries ?? [];
  const authorizationEntries = authorizationData?.entries ?? [];
  const authLetters = authorizationEntries.map((item) => {
    return {
      value: item.blob_path ?? "",
      label: item.description ?? "",
    };
  });
  const getAllJCodesData = () => {
    let JcodesData: JCodeDetails[] = [];
    authEntries.forEach((item) => {
      JcodesData.push(...(item?.j_codes ?? []));
    });
    return JcodesData;
  };
  const JcodesData = getAllJCodesData();

  const allMedicineNames: string[] = [];
  JcodesData.forEach((item) => {
    if (item?.medicine_name && Array.isArray(item.medicine_name)) {
      allMedicineNames.push(...item.medicine_name);
    }
  });

  UserStore.setItem(
    `${UserGeneralInfoContextKeys.MEDICINE_NAMES}_${generalOrderDetails?.order_id}`,
    allMedicineNames,
  );
  const allKeys = Object.keys(statusToDocumentUploadMap);

  return JcodesData.map((item) => {
    const description = () => {
      if (item?.medicine_name && item?.medicine_name !== "") {
        return item?.medicine_name;
      }
      return item?.description ?? "N/A";
    };

    const globalAuthStatus = authOnFileDetails?.auth_status || "not_available";

    const authStatus = item?.auth_status || "not_available";

    const allDocsAreUploaded = checkifAllDocsAreUploaded(
      authStatus,
      item?.documents ?? [],
      item?.id ?? "",
      item?.j_code ?? "",
    );

    const docsNotBelongingToCurrentAuthStatus =
      getDocsNotBelongingToCurrentAuthStatus(
        authStatus,
        item?.documents ?? [],
        item?.j_code ?? "",
        item?.id ?? "",
      );

    return {
      [CptCodeWithDetailsKeys.JCodes]: item?.j_code + " - " + description(),
      [CptCodeWithDetailsKeys.JCodeID]: item?.id ?? "N/A",
      [CptCodeWithDetailsKeys.Documents]: {
        authStatus,
        docs: authStatusForDisablingTriggerRpa.includes(globalAuthStatus)
          ? []
          : [...docsNotBelongingToCurrentAuthStatus, ...allDocsAreUploaded],
        doc_upload_status:
          generalOrderDetails?.status?.document_upload_status ??
          DocWriteBackStatus.NOT_INITIATED,
        patient_id:
          generalOrderDetails?.demographics?.patient_id ??
          generalOrderDetails?.demographics?.patient_mrn ??
          "",
        order_id: generalOrderDetails?.order_id ?? "",
        shouldLogEvent: true,
        documentsList: authLetters,
        documentsLoading: authorizationDataLoading,
      },
      [CptCodeWithDetailsKeys.DocWithUploadIcon]: {
        status:
          getDocumentUploadStatusForJcode(item) ||
          DocWriteBackStatus.NOT_INITIATED,
        shouldHideUploadIcon:
          !allKeys.includes(authStatus) ||
          authStatusForDisablingTriggerRpa.includes(globalAuthStatus),
        rowData: item,
      },
    };
  });
};

/**
 * @param authStatusId - Master or line-level auth status id
 * @returns Document types required for that status (from status-to-doc map)
 */
export const getDocumentTypesForAuthStatus = (authStatusId: string) => {
  return getDocumentsForStatus(authStatusId);
};

/**
 * @param authStatusId - Auth status id used for upload-status mapping
 * @returns Expected document upload statuses for that auth status
 */
export const getDocumentUploadStatusForAuthStatus = (authStatusId: string) => {
  return getDocumentsUploadStatusForStatus(authStatusId);
};

/**
 * Human-readable upload action label for a {@link DocumentTypesForOrder} value.
 *
 * @param documentType - Internal document type enum/string
 * @returns UI string for buttons or empty states
 */
export const getDocumentTypeDisplayName = (documentType: string) => {
  switch (documentType) {
    case DocumentTypesForOrder.FILLED_FORM:
      return "Upload filled form";
    case DocumentTypesForOrder.APPROVAL_LETTER:
      return "Upload approval letter";
    case DocumentTypesForOrder.DENIAL_LETTER:
      return "Upload Denial Letter";
    case DocumentTypesForOrder.NAR_LETTER:
      return "NAR Letter";
    case DocumentTypesForOrder.FAX_LETTER:
      return "Fax Letter";
    default:
      return "Upload Required Document";
  }
};
